/**
 * [비즈니스 목적]
 * 백엔드 REST API 통신 서비스 (Singleton)
 *
 * [워크플로우]
 * 1. axios 클라이언트 초기화 (baseURL, timeout, withCredentials 설정)
 * 2. 요청 인터셉터: Google OAuth 토큰을 Authorization 헤더에 자동 추가
 * 3. 응답 인터셉터: 에러 처리 및 로깅
 * 4. RESTful API 메서드 제공 (GET, POST, PUT, DELETE)
 *
 * [주요 기능 카테고리]
 * 1. Tenants API - 테넌트(고객사) CRUD
 * 2. File Ontology API - 파일 온톨로지 메타데이터 관리
 * 3. File Prompt API - 파일-프롬프트 N:N 연결 관리
 * 4. Prompts API - 재사용 가능한 프롬프트 마스터 관리
 * 5. Conversions API - Cloud Run Job 배치 변환, Python 변환, Fallback 상태 조회
 * 6. Markdown API - 시트별 MD 파일 저장/로드
 * 7. Excel Import/Export - 테넌트 데이터 엑셀 다운로드/업로드
 *
 * [아키텍처]
 * - Singleton 패턴 (단일 인스턴스 export)
 * - axios 인터셉터를 통한 중앙화된 인증 및 에러 처리
 * - baseURL: process.env.REACT_APP_API_BASE_URL (프로덕션은 빈 문자열 - Nginx 프록시)
 *
 * [사용 예시]
 * import apiService from '../services/api';
 * const response = await apiService.getFiles(tenantCode);
 * if (response.success) { ... }
 */

import axios from 'axios';

class APIService {
  constructor() {
    // Empty string means use relative path (same origin) - for Nginx proxy setup
    this.baseURL = process.env.REACT_APP_API_BASE_URL !== undefined
      ? process.env.REACT_APP_API_BASE_URL
      : 'http://localhost:5000';
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      withCredentials: true,  // CORS credentials 허용
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // 요청 인터셉터 - Firebase Auth 토큰 추가
    this.client.interceptors.request.use(
      async (config) => {
        try {
          // Local/QA 환경: Mock token 사용
          if (process.env.REACT_APP_DISABLE_AUTH === 'true') {
            config.headers.Authorization = 'Bearer mock-token-local';
            return config;
          }

          // Get Firebase ID token from current user
          const { auth } = await import('../firebase');
          const firebaseUser = auth.currentUser;
          const token = firebaseUser ? await firebaseUser.getIdToken() : null;

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to add authentication token:', error);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 응답 인터셉터 - 에러 처리
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('API 요청 오류:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            message: error.response?.data?.error || error.message
          });
        }
        
        return Promise.reject(error);
      }
    );
  }

  // ============================
  // Tenants API (RESTful)
  // ============================

  /**
   * 모든 테넌트 목록 조회
   * GET /api/ontology/tenants
   */
  async getAllTenants() {
    try {
      const response = await this.client.get('/api/ontology/tenants');
      return response.data;
    } catch (error) {
      throw new Error(`테넌트 목록 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 모든 테넌트 목록 조회 (별칭)
   * GET /api/ontology/tenants
   */
  async getTenants() {
    return this.getAllTenants();
  }

  /**
   * 특정 테넌트 조회
   * GET /api/ontology/tenants/:id
   */
  async getTenant(tenantId) {
    try {
      const response = await this.client.get(`/api/ontology/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      throw new Error(`테넌트 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }
  
  /**
   * 테넌트 생성
   * POST /api/ontology/tenants
   */
  async createTenant(tenantData) {
    try {
      const response = await this.client.post('/api/ontology/tenants', tenantData);
      return response.data;
    } catch (error) {
      throw new Error(`테넌트 생성 실패: ${error.response?.data?.error || error.message}`);
    }
  }
  
  /**
   * 테넌트 수정 (tenant_codes 포함 전체 수정)
   * PUT /api/ontology/tenants/:id
   */
  async updateTenant(tenantId, tenantData) {
    try {
      const response = await this.client.put(`/api/ontology/tenants/${tenantId}`, tenantData);
      return response.data;
    } catch (error) {
      throw new Error(`테넌트 수정 실패: ${error.response?.data?.error || error.message}`);
    }
  }
  
  /**
   * 테넌트 삭제
   * DELETE /api/ontology/tenants/:id
   */
  async deleteTenant(tenantId) {
    try {
      const response = await this.client.delete(`/api/ontology/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      throw new Error(`테넌트 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 테넌트 에이전트(K8s 파드) 삭제
   * DELETE /api/agent-proxy/admin/tenants/:id
   *
   * 동작 (api-gateway tenant-admin-routes.ts:125):
   *   1. Deployment scale → 0 (파드 종료)
   *   2. Deployment 삭제
   *   3. Service 삭제
   *   4. ConfigMap 에서 제거
   *
   * GCS 버킷의 데이터는 보존됨.
   *
   * @param {string} tenantCode - 8자리 hex (예: c8cd3500). api-gateway 는 7자리로 검증하므로
   *   호출 측에서 슬라이스하여 전달해야 함 (예: 'c8cd350').
   */
  async deleteTenantAgent(tenantCode) {
    try {
      const response = await this.client.delete(`/api/agent-proxy/admin/tenants/${tenantCode}`);
      return response.data;
    } catch (error) {
      // k8s ConfigMap 에 없는 테넌트(DB-only 또는 이미 정리됨)는 idempotent success 로 처리.
      // api-gateway tenant-admin-routes.ts:136 가 404 를 반환하지만, REST 멱등성 원칙상
      // "이미 없는 리소스를 지우는 것" 은 성공으로 보아야 한다.
      if (error.response?.status === 404) {
        return { success: true, alreadyGone: true, deleted: tenantCode };
      }
      throw new Error(`에이전트 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 테넌트 에이전트(K8s 파드 + ConfigMap + Service) 생성
   * POST /api/agent-proxy/admin/tenants
   *
   * 동작 (api-gateway tenant-admin-routes.ts:31):
   *   1. ConfigMap 에 entry 추가
   *   2. Deployment 생성 (replicas: 0 — 트래픽 들어올 때 KEDA 가 scale up)
   *   3. Service 생성
   *
   * ⚠️ GCS 버킷(`hyperlounge-{code}-foldersync` + `hyperlounge-{code}`)은
   *    이 endpoint 가 만들지 않음. 미리 생성되어 있어야 파드 mount 가 동작.
   *
   * @param {object} payload
   * @param {string} payload.id           7자리 hex (예: 'ctest01')
   * @param {string} payload.name         영문/식별용 이름
   * @param {string} payload.nameKorean   UI 표시명
   * @param {string} payload.bucket       foldersync 버킷명 (보통 hyperlounge-{8자리}-foldersync)
   * @param {object} payload.identity     { name, emoji }
   * @param {boolean} [payload.enabled]   기본 true
   */
  async createTenantAgent(payload) {
    try {
      const response = await this.client.post('/api/agent-proxy/admin/tenants', payload);
      return response.data;
    } catch (error) {
      throw new Error(`에이전트 생성 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // File Ontology API
  // ============================

  /**
   * 파일 온톨로지 목록 조회
   */
  async getFiles(tenantId = null) {
    try {
      const params = {};
      if (tenantId && tenantId !== 'all') {
        // 테넌트 코드로 전달 (숫자 ID가 아닌 코드)
        params.tenant_code = tenantId;
      }

      const response = await this.client.get('/api/ontology/files', { params });
      return response.data;
    } catch (error) {
      throw new Error(`파일 목록 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * LLM 변환 가능한 파일 목록 조회
   */
  async getLlmEnabledFiles(tenantCode) {
    try {
      const response = await this.client.get('/api/conversions/llm-targets', {
        params: { tenant_code: tenantCode }
      });
      return {
        success: true,
        data: response.data.files || []
      };
    } catch (error) {
      console.error('LLM 파일 목록 조회 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        data: []
      };
    }
  }

  /**
   * Python 스크립트 조회
   * @param {string} tenantCode - 테넌트 코드
   * @param {string} promptId - 프롬프트 ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  async getPythonScript(tenantCode, promptId) {
    try {
      const response = await this.client.get('/api/conversions/python-script', {
        params: { tenant_code: tenantCode, prompt_id: promptId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Python 스크립트 조회 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  }

  /**
   * 파일 온톨로지 생성
   */
  async createFile(fileData) {
    try {
      const response = await this.client.post('/api/ontology/files', fileData);
      return response.data;
    } catch (error) {
      throw new Error(`파일 생성 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일 온톨로지 수정
   */
  async updateFile(fileId, fileData) {
    try {
      console.log('🔄 API updateFile 호출:', {
        fileId,
        url: `/api/ontology/files/${fileId}`,
        hasSheetDetails: !!fileData.sheetDetails,
        sheetDetailsLength: fileData.sheetDetails?.length
      });
      
      // 요청 전 데이터 확인
      console.log('📤 API 요청 데이터:', {
        fileName: fileData.file_name,
        sheetDetails: fileData.sheetDetails
      });
      
      const response = await this.client.put(`/api/ontology/files/${fileId}`, fileData);
      
      console.log('✅ API updateFile 응답:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API updateFile 오류:', {
        fileId,
        error: error.message,
        response: error.response
      });
      throw new Error(`파일 수정 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일 온톨로지 삭제
   */
  async deleteFile(fileId) {
    try {
      const response = await this.client.delete(`/api/ontology/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw new Error(`파일 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일 온톨로지 통계 조회
   */
  async getFileStats(tenantId = null) {
    try {
      const params = {};
      if (tenantId && tenantId !== 'all') {
        // 테넌트 코드로 전달 (숫자 ID가 아닌 코드)
        params.tenant_code = tenantId;
      }

      const response = await this.client.get('/api/ontology/files/stats', { params });
      return response.data;
    } catch (error) {
      throw new Error(`통계 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // Markdown 파일 관련 API
  // ============================

  /**
   * Markdown 파일 저장 (시트별)
   * @param {string} sheetId - 시트 ID
   * @param {object} data - { content, tenantCode, fileId }
   */
  async saveMarkdown(sheetId, data) {
    try {
      const { content, tenantCode, fileId } = data;
      const response = await this.client.post(
        `/api/ontology/files/sheets/${sheetId}/markdown?tenant_code=${tenantCode}&file_id=${fileId}`,
        { content }
      );
      return response.data;
    } catch (error) {
      throw new Error(`MD 파일 저장 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Markdown 파일 로드 (시트별)
   * @param {string} sheetId - 시트 ID
   * @param {string} tenantCode - 테넌트 코드
   * @param {string} fileId - 파일 ID
   */
  async loadMarkdown(sheetId, tenantCode, fileId) {
    try {
      const response = await this.client.get(
        `/api/ontology/files/sheets/${sheetId}/markdown?tenant_code=${tenantCode}&file_id=${fileId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(`MD 파일 로드 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // Excel Import/Export
  // ============================

  /**
   * 테넌트 전체 데이터 엑셀 다운로드
   * @param {string} tenant_code - 테넌트 코드
   */
  async downloadExcel(tenant_code) {
    try {
      const response = await this.client.get(
        `/api/ontology-excel/export?tenant_code=${tenant_code}`,
        {
          responseType: 'blob', // 파일 다운로드를 위한 blob 타입
        }
      );

      // 파일명 생성 (테넌트 코드 + 날짜)
      const filename = `${tenant_code}_ontology_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Blob을 이용한 다운로드
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      throw new Error(`엑셀 다운로드 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 엑셀 업로드 및 검증
   * @param {File} file - 업로드할 엑셀 파일
   * @param {string} tenant_code - 테넌트 코드
   */
  async uploadExcel(file, tenant_code) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_code', tenant_code);

      const response = await this.client.post('/api/ontology-excel/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(`엑셀 업로드 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 변경사항 적용
   * @param {Object} fileData - 파일 데이터
   * @param {Array} sheets - 시트 데이터
   * @param {string} tenant_code - 테넌트 코드
   */
  async applyExcelChanges(fileData, sheets, tenant_code) {
    try {
      const response = await this.client.post('/api/ontology-excel/apply', {
        fileData,
        sheets,
        tenant_code,
      });

      return response.data;
    } catch (error) {
      throw new Error(`변경사항 적용 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // File Prompt API (Ontology 2.0)
  // ============================

  /**
   * 파일 프롬프트 목록 조회
   * GET /api/file-prompt
   */
  async getFilePrompts(tenantCode = null, fileId = null) {
    try {
      const params = {};
      if (tenantCode && tenantCode !== 'all') {
        params.tenant_code = tenantCode;
      }
      if (fileId) {
        params.file_id = fileId;
      }

      const response = await this.client.get('/api/file-prompt', { params });
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 목록 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 특정 파일 프롬프트 조회
   * GET /api/file-prompt/:id
   */
  async getFilePrompt(id) {
    try {
      const response = await this.client.get(`/api/file-prompt/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일 프롬프트 생성
   * POST /api/file-prompt
   */
  async createFilePrompt(promptData) {
    try {
      const response = await this.client.post('/api/file-prompt', promptData);
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 생성 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일 프롬프트 수정
   * PUT /api/file-prompt/:id
   */
  async updateFilePrompt(id, promptData) {
    try {
      const response = await this.client.put(`/api/file-prompt/${id}`, promptData);
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 수정 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 특정 파일-프롬프트 연결 삭제 (N:N 관계)
   * DELETE /api/file-prompt/:tenant_code/:file_id/:prompt_id
   */
  async deleteFilePrompt(tenantCode, fileId, promptId) {
    try {
      const response = await this.client.delete(
        `/api/file-prompt/${tenantCode}/${fileId}/${promptId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 파일의 모든 프롬프트 연결 삭제
   * DELETE /api/file-prompt/:tenant_code/:file_id
   */
  async deleteAllFilePrompts(tenantCode, fileId) {
    try {
      const response = await this.client.delete(
        `/api/file-prompt/${tenantCode}/${fileId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(`파일 프롬프트 전체 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // Prompts API (재사용 가능한 프롬프트 마스터)
  // ============================

  /**
   * 프롬프트 목록 조회
   * GET /api/prompts
   */
  async getPrompts(tenantCode = null) {
    try {
      const params = {};
      if (tenantCode && tenantCode !== 'all') {
        params.tenant_code = tenantCode;
      }

      const response = await this.client.get('/api/prompts', { params });
      return response.data;
    } catch (error) {
      throw new Error(`프롬프트 목록 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 특정 프롬프트 조회
   * GET /api/prompts/:tenant_code/:prompt_id
   */
  async getPrompt(tenantCode, promptId) {
    try {
      const response = await this.client.get(`/api/prompts/${tenantCode}/${promptId}`);
      return response.data;
    } catch (error) {
      throw new Error(`프롬프트 조회 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 프롬프트 생성/수정 (Upsert)
   * POST /api/prompts
   */
  async createPrompt(promptData) {
    try {
      const response = await this.client.post('/api/prompts', promptData);
      return response.data;
    } catch (error) {
      throw new Error(`프롬프트 저장 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 프롬프트 수정
   * PUT /api/prompts/:tenant_code/:prompt_id
   */
  async updatePrompt(tenantCode, promptId, promptData) {
    try {
      const response = await this.client.put(`/api/prompts/${tenantCode}/${promptId}`, promptData);
      return response.data;
    } catch (error) {
      throw new Error(`프롬프트 수정 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * 프롬프트 삭제
   * DELETE /api/prompts/:tenant_code/:prompt_id
   */
  async deletePrompt(tenantCode, promptId) {
    try {
      const response = await this.client.delete(`/api/prompts/${tenantCode}/${promptId}`);
      return response.data;
    } catch (error) {
      throw new Error(`프롬프트 삭제 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Python 코드 가져오기
   * GET /api/prompts/:tenant_code/:prompt_id/python-code
   * @returns {Promise<{success, data: {code, path, editable, ...}, source}>}
   */
  async getPythonCode(tenantCode, promptId) {
    try {
      const response = await this.client.get(`/api/prompts/${tenantCode}/${promptId}/python-code`);
      return response.data;
    } catch (error) {
      throw new Error(`Python 코드 가져오기 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Python 코드 저장
   * PUT /api/prompts/:tenant_code/:prompt_id/python-code
   * @param {string} tenantCode - 테넌트 코드
   * @param {string} promptId - 프롬프트 ID
   * @param {string} code - Python 코드 내용
   * @returns {Promise<{success, message, data: {path, size, updated}}>}
   */
  async savePythonCode(tenantCode, promptId, code) {
    try {
      const response = await this.client.put(`/api/prompts/${tenantCode}/${promptId}/python-code`, { code });
      return response.data;
    } catch (error) {
      throw new Error(`Python 코드 저장 실패: ${error.response?.data?.error || error.message}`);
    }
  }

  // ============================
  // Conversions API (Cloud Run Job & Python Converter)
  // ============================

  /**
   * Cloud Run Job 배치 변환 실행
   * POST /api/conversions/batch
   * @param {object} params - { tenant_code, bsda, file_id?, prompt_id?, model? }
   * @returns {Promise<object>} { status, execution_name, job_name, ... }
   */
  async executeBatchConversion(params) {
    try {
      const response = await this.client.post('/api/conversions/batch', params);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('배치 변환 실행 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Cloud Run Job 실행 상태 조회 (변환 결과 포함)
   * GET /api/conversions/batch/:executionName
   * @param {string} executionName - Cloud Run Job execution name
   * @param {string} tenant_code - 테넌트 코드 (변환 결과 조회용)
   * @param {string} bsda - 기준일시 (변환 결과 조회용)
   * @returns {Promise<object>} Job status with conversion_results[]
   */
  async getBatchExecutionStatus(executionName, tenant_code, bsda) {
    try {
      const params = {};
      if (tenant_code) params.tenant_code = tenant_code;
      if (bsda) params.bsda = bsda;

      const response = await this.client.get(`/api/conversions/batch/${executionName}`, { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('배치 실행 상태 조회 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Python Converter로 파일 변환
   * POST /api/conversions/python-convert
   * @param {object} params - { tenant_code, bsda, file_id?, prompt_id? }
   * @returns {Promise<object>} Conversion results with download URLs
   */
  async executePythonConversion(params) {
    try {
      const response = await this.client.post('/api/conversions/python-convert', params);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Python 변환 실행 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Claude Fallback 진행 상태 조회
   * GET /api/conversions/fallback-status
   * @param {string} tenantCode - 테넌트 코드
   * @param {string} bsda - 기준일시 (선택)
   * @param {string} fileId - 파일 ID (선택)
   * @returns {Promise<object>} { has_active_fallback, fallback_items, message }
   */
  async getFallbackStatus(tenantCode, bsda = null, fileId = null) {
    try {
      const params = { tenant_code: tenantCode };
      if (bsda) params.bsda = bsda;
      if (fileId) params.file_id = fileId;

      const response = await this.client.get('/api/conversions/fallback-status', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Fallback 상태 조회 실패:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        data: { has_active_fallback: false, fallback_items: [], message: '' }
      };
    }
  }

  // ============================
  // 헬스 체크
  // ============================

  /**
   * 백엔드 서버 헬스 체크
   */
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(`헬스 체크 실패: ${error.response?.data?.error || error.message}`);
    }
  }
}

// 싱글톤 인스턴스 생성
const apiService = new APIService();

export default apiService;